/**
 * Chat session lifecycle in the browser: creates or restores a session, keeps the SSE stream
 * open (reconnecting from the last seen event) and exposes the user actions.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { CHAT_SSE_EVENT, type ChatEvent } from '../../../shared/api.js';
import { api, ApiError } from '../lib/api';
import { parseSse } from '../lib/sse';
import { applyChatEvent, initialChatState, type ChatState } from './state';

const SESSION_KEY = 'cybernika.chat.session';
const RETRY_DELAYS_MS = [500, 1_000, 2_000, 5_000, 10_000];

type Action = { type: 'event'; event: ChatEvent } | { type: 'reset' };

function reducer(state: ChatState, action: Action): ChatState {
  return action.type === 'reset' ? initialChatState : applyChatEvent(state, action.event);
}

function loadSessionId(): string | undefined {
  try {
    return sessionStorage.getItem(SESSION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function saveSessionId(id: string | undefined): void {
  try {
    if (id) sessionStorage.setItem(SESSION_KEY, id);
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface UseChat {
  state: ChatState;
  connection: ConnectionState;
  error: string | undefined;
  send(text: string): Promise<void>;
  approve(requestId: string, allow: boolean): Promise<void>;
  interrupt(): Promise<void>;
  newChat(): Promise<void>;
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function useChat(enabled: boolean): UseChat {
  const [state, dispatch] = useReducer(reducer, initialChatState);
  const [sessionId, setSessionId] = useState<string | undefined>(loadSessionId);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [error, setError] = useState<string | undefined>();
  const lastSeq = useRef(-1);
  const queryClient = useQueryClient();

  useEffect(() => {
    lastSeq.current = state.lastSeq;
  }, [state.lastSeq]);

  const adoptSession = useCallback(
    (id: string) => {
      saveSessionId(id);
      lastSeq.current = -1;
      dispatch({ type: 'reset' });
      setSessionId(id);
      // The overview shows the number of active sessions.
      void queryClient.invalidateQueries({ queryKey: ['status'] });
    },
    [queryClient],
  );

  // Create a session when none is stored.
  useEffect(() => {
    if (!enabled || sessionId) return;
    let cancelled = false;
    api.createSession().then(
      ({ sessionId: id }) => {
        // StrictMode runs effects twice: close the session nobody will use.
        if (cancelled) void api.closeSession(id).catch(() => undefined);
        else adoptSession(id);
      },
      (err: unknown) => {
        if (!cancelled) setError(describe(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, sessionId, adoptSession]);

  // Keep the event stream open for the current session.
  useEffect(() => {
    if (!enabled || !sessionId) return;
    const controller = new AbortController();
    let attempt = 0;

    const run = async (): Promise<void> => {
      while (!controller.signal.aborted) {
        setConnection(attempt === 0 ? 'connecting' : 'reconnecting');
        try {
          const response = await api.events(sessionId, lastSeq.current, controller.signal);
          if (!response.body) throw new Error('Streaming is not supported by this browser');
          setConnection('open');
          setError(undefined);
          attempt = 0;
          for await (const message of parseSse(response.body)) {
            if (message.event !== CHAT_SSE_EVENT) continue;
            const event = JSON.parse(message.data) as ChatEvent;
            lastSeq.current = Math.max(lastSeq.current, event.seq);
            dispatch({ type: 'event', event });
            if (event.type === 'session_closed') {
              setConnection('closed');
              return;
            }
          }
        } catch (err) {
          if (controller.signal.aborted) return;
          if (err instanceof ApiError && err.status === 404) {
            // The server restarted and forgot the session.
            saveSessionId(undefined);
            setSessionId(undefined);
            return;
          }
          if (err instanceof ApiError && err.status === 401) {
            setConnection('closed');
            setError(err.message);
            return;
          }
          setError(describe(err));
        }
        const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] ?? 10_000;
        attempt += 1;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    };

    void run();
    return () => controller.abort();
  }, [enabled, sessionId]);

  const send = useCallback(
    async (text: string) => {
      if (!sessionId) return;
      setError(undefined);
      try {
        await api.sendMessage(sessionId, text);
      } catch (err) {
        setError(describe(err));
        throw err;
      }
    },
    [sessionId],
  );

  const approve = useCallback(
    async (requestId: string, allow: boolean) => {
      if (!sessionId) return;
      try {
        await api.resolveApproval(sessionId, requestId, allow);
      } catch (err) {
        setError(describe(err));
      }
    },
    [sessionId],
  );

  const interrupt = useCallback(async () => {
    if (!sessionId) return;
    try {
      await api.interrupt(sessionId);
    } catch (err) {
      setError(describe(err));
    }
  }, [sessionId]);

  const newChat = useCallback(async () => {
    const old = sessionId;
    setError(undefined);
    try {
      const { sessionId: id } = await api.createSession();
      adoptSession(id);
    } catch (err) {
      setError(describe(err));
      return;
    }
    if (old) await api.closeSession(old).catch(() => undefined);
  }, [sessionId, adoptSession]);

  return { state, connection, error, send, approve, interrupt, newChat };
}
